import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, of, forkJoin, Subject } from 'rxjs';
import { map, catchError, tap, finalize, switchMap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { SmartScopeProjectData } from 'src/app/shared/models/project-data.model';
import { VendorOnboardingTracker } from 'src/app/shared/analytics/vendor-onboarding.tracker';

// ─── User Info ────────────────────────────────────────────────────────
export interface UserInfo {
  name: string;
  email: string;
  visibility: string; // 'local' | 'customer' | 'vendor'
  team: string; // custom:team (vendor code / contributor code)
  firstName: string;
  initials: string;
  isVendor: boolean;
  isCustomer: boolean;
  isLocal: boolean;
  roleLabel: string; // 'Vendor' | 'Client' | 'Team'
}

// ─── Project (from API) ──────────────────────────────────────────────
export interface ProjectRaw {
  id: string;
  title: string;
  customer: string;
  type: string;
  status: string;
  budget: number;
  vendorCost: number;
  deliveryDate: string;
  createdDate: string;
  currentDate: string;
  lead: string;
  owner: { name: string };
  comment: string;
  acceptForBid: boolean;
  completed: boolean;
  stgplan: boolean;
  stgres: boolean;
  stgdel: boolean;
  notifications: any[];
  invited: { [vendorCode: string]: any };
  VendorAssingedDetail: { vendor_code: string; vendor_status: string }[];
  created_by: string;
}

// ─── Mapped project for UI ───────────────────────────────────────────
export interface MappedProject {
  id: string;
  title: string;
  customer: string;
  type: string;
  status: string;
  budget: number;
  vendorCost: number;
  price: number;
  deliveryDate: string;
  createdDate: Date;
  lastUpdatedDate: string;
  lead: string;
  owner: string;
  comment: string;
  acceptForBid: boolean;
  completed: boolean;
  stgplan: boolean;
  stgres: boolean;
  stgdel: boolean;
  notifications: any[];
  vendorStatus?: string; // 'pending' | 'accepted' | 'rejected'
  revisionRequested?: boolean;
  revisedBudget?: number;
  category: 'invited' | 'assigned' | 'completed' | 'inprogress' | 'other';
}

// ─── Vendor profile (submission-mahatta DB) ───────────────────────────
export interface VendorProfile {
  contributor_id?: number;
  name: string;
  agency_name: string;
  email: string;
  mobile: string;
  alt_phone_no: string;
  alt_email: string;
  whatsapp: string;
  account_type_id: number; // 1 = Individual, 2 = Agency
  // Financial
  bank_id: number | null;
  pan_card: string;
  account_holder_name: string;
  account_number: string;
  ifsc: string;
  branch_address: string;
  upi: string;
  paypal: string;
  is_basic_details_completed: boolean;
}

export interface Bank {
  id: number;
  name: string;
}

// ─── Vendor stats from /vendors/:code ────────────────────────────────
export interface VendorStats {
  totalBidsPlaced: number;
  bidsApproved: number;
  bidsRejected: number;
  pendingBids: number;
  onGoingProjects: number;
  totalEarnings: number;
  averageApproval: string;
  vendorCode: string;
  newBids: number;
  proposalsAccepted?:number;
  projects: { [key: string]: VendorStatsProject };
}

export interface VendorStatsProject {
  invited: boolean;
  assigned: boolean;
  rejected: boolean;
  totalAmount: number;
  pendingAmount: number;
  advancedAmount: number;
  startDate?: string;
  endDate?: string;
  title?: string;
  proposalAccepted?: boolean;
  onHold?: boolean;
  cancelLost?: boolean;
  abandoned?: boolean;
  expires?: boolean;
  completed?: boolean;
}

export interface ProfileSectionProgress {
  basic: {
    completed: boolean;
    fields: {
      name: boolean;
      email: boolean;
      phone: boolean;
      altPhone: boolean;      // Optional - bonus only
      altEmail: boolean;      // Optional - bonus only
      whatsapp: boolean;      // Optional - bonus only
    };
    requiredFields: string[];
    weight: number; // 25%
  };
  financial: {
    completed: boolean;
    fields: {
      paymentMethod: boolean;
      panCard: boolean;       // Optional - bonus only
      accountDetails: boolean; // Optional - bonus only
    };
    requiredFields: string[];
    weight: number; // 15%
  };
  identity: {
    completed: boolean;
    fields: {
      shortBio: boolean;
      locationAvailability: boolean; // Optional - bonus only
    };
    requiredFields: string[];
    weight: number; // 25%
  };
  showreels: {
    completed: boolean;
    fields: {
      hasSamples: boolean;
      minSamplesReached: boolean; // Optional bonus (3+ samples)
      aiEvaluated: boolean;       // Optional bonus
    };
    requiredFields: string[];
    weight: number; // 35%
  };
}

export interface ProfileProgress {
  overallPercent: number;
  sections: ProfileSectionProgress;
  isBidReady: boolean;
  missingRequirements: string[];
  nextSteps: string[];
}

@Injectable({ providedIn: 'root' })
export class VendorConsoleService {
  private apiUrl = environment.apiUrl;

  // ─── State subjects ─────────────────────────────────────────────
  private userSubject = new BehaviorSubject<UserInfo | null>(null);
  user$ = this.userSubject.asObservable();

  private projectsSubject = new BehaviorSubject<MappedProject[]>([]);
  projects$ = this.projectsSubject.asObservable();

   vendorStatsSubject = new BehaviorSubject<VendorStats | null>(null);
  vendorStats$ = this.vendorStatsSubject.asObservable();

  private loadingSubject = new BehaviorSubject<boolean>(false);
  loading$ = this.loadingSubject.asObservable();

  private errorSubject = new BehaviorSubject<string | null>(null);
  error$ = this.errorSubject.asObservable();

  contributorId: number | null = null;

  private basicProfileCompletedSubject = new BehaviorSubject<boolean>(false);
  basicProfileCompleted$ = this.basicProfileCompletedSubject.asObservable();

  // ─── Profile Progress State ─────────────────────────────────────
  private basicProfileFieldsSubject = new BehaviorSubject<Partial<ProfileSectionProgress['basic']['fields']>>({});
  private financialFieldsSubject = new BehaviorSubject<Partial<ProfileSectionProgress['financial']['fields']>>({});
  private identityFieldsSubject = new BehaviorSubject<Partial<ProfileSectionProgress['identity']['fields']>>({});
  private showreelsFieldsSubject = new BehaviorSubject<Partial<ProfileSectionProgress['showreels']['fields']>>({});

  private profileProgressSubject = new BehaviorSubject<ProfileProgress>(this.getDefaultProgress());
  profileProgress$ = this.profileProgressSubject.asObservable();

  get isBasicProfileCompleted(): boolean {
    return this.basicProfileCompletedSubject.value;
  }

  setBasicProfileCompleted(value: boolean): void {
    this.basicProfileCompletedSubject.next(value);
  }

  constructor(
    private http: HttpClient,
    private onboardingTracker: VendorOnboardingTracker,
  ) {}

  // ─── User info from localStorage ───────────────────────────────
  loadUser(): UserInfo {
    const raw = localStorage.getItem('userInfo');
    if (!raw) {
      const guest: UserInfo = {
        name: 'Guest',
        email: '',
        visibility: 'local',
        team: '',
        firstName: 'Guest',
        initials: 'G',
        isVendor: false,
        isCustomer: false,
        isLocal: true,
        roleLabel: 'Team',
      };
      this.userSubject.next(guest);
      return guest;
    }

    const parsed = JSON.parse(raw);
    const name = parsed.name || 'User';
    const visibility =
      parsed['custom:visibility'] || parsed.visibility || 'local';
    const team = parsed['custom:team'] || parsed.team || '';
    const parts = name.split(' ');
    const firstName = parts[0] || 'User';
    const initials = parts
      .map((p: string) => p.charAt(0).toUpperCase())
      .join('')
      .substring(0, 2);

    const user: UserInfo = {
      name,
      email: parsed.email || '',
      visibility,
      team,
      firstName,
      initials,
      isVendor: visibility === 'vendor',
      isCustomer: visibility === 'customer',
      isLocal: visibility === 'local',
      roleLabel:
        visibility === 'vendor'
          ? 'Vendor'
          : visibility === 'customer'
            ? 'Client'
            : 'Team',
    };

    this.userSubject.next(user);
    return user;
  }

  getUser(): UserInfo | null {
    return this.userSubject.value;
  }

  // ─── Fetch projects from API ───────────────────────────────────
  fetchProjects(): Observable<MappedProject[]> {
    const user = this.getUser() || this.loadUser();
    this.loadingSubject.next(true);
    this.errorSubject.next(null);

    const url = `${this.apiUrl}projects?user=${encodeURIComponent(user.name)}&visibility=${encodeURIComponent(user.visibility)}&team=${encodeURIComponent(user.team)}&email=${encodeURIComponent(user.email)}`;

    return this.http.get<ProjectRaw[]>(url).pipe(
      map((projects) => this.mapAndCategorize(projects, user)),
      tap((mapped) => this.projectsSubject.next(mapped)),
      catchError((err) => {
        console.error('Error fetching projects:', err);
        this.errorSubject.next('Failed to load projects. Please try again.');
        return of([]);
      }),
      finalize(() => this.loadingSubject.next(false)),
    );
  }

  // ─── Fetch vendor stats ────────────────────────────────────────
  fetchVendorStats(): Observable<VendorStats | null> {
    const user = this.getUser() || this.loadUser();
    if (!user.team) return of(null);

    return this.http
      .get<VendorStats>(`${this.apiUrl}vendors/${user.team}`)
      .pipe(
        tap((stats) => this.vendorStatsSubject.next(stats)),
        catchError((err) => {
          console.error('Error fetching vendor stats:', err);
          return of(null);
        }),
      );
  }

  // ─── Load everything ───────────────────────────────────────────
  loadAll(): Observable<{
    projects: MappedProject[];
    stats: VendorStats | null;
  }> {
    this.loadUser();
    return forkJoin({
      projects: this.fetchProjects(),
      stats: this.fetchVendorStats(),
    });
  }

  // ─── Map raw projects into categorized UI-friendly format ──────
  private mapAndCategorize(raw: ProjectRaw[], user: UserInfo): MappedProject[] {
    const sorted = (raw || []).sort((a, b) => {
      return (
        new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime()
      );
    });
    const vendorStats = this.vendorStatsSubject.value;
    const statsProjects = vendorStats?.projects || {};

    return sorted.map((item) => {
      const team = user.team;
      const isInvited = item.invited && item.invited[team] !== undefined;
      const vendorAssignment = (item.VendorAssingedDetail || []).find(
        (v) => v.vendor_code === team,
      );
      const vendorStatus = vendorAssignment?.vendor_status;
      const hasBid = !!item.invited?.[team]?.bid_price;
      const totalAmounts = statsProjects[item.id]?.totalAmount > 0;
      let category: MappedProject['category'] = 'other';
      if (user.isVendor) {
        if (
          (item.status === 'Completed' || item.status === 'Abandoned') &&
          vendorAssignment &&
          vendorStatus === 'accepted'
        ) {
          category = 'completed';
        } else if (
          isInvited &&
          vendorAssignment &&
          (vendorStatus === 'pending' || vendorStatus === 'accepted')
        ) {
          category = 'assigned';
        } else if (isInvited && !vendorAssignment && !hasBid && !totalAmounts) {
          category = 'invited';
        }
      } else {
        // client or local — use project status
        if (item.status === 'Completed' || item.status === 'Abandoned') {
          category = 'completed';
        } else if (item.acceptForBid) {
          category = 'assigned'; // accepted for bid = active
        } else {
          category = 'inprogress';
        }
      }

      return {
        id: item.id,
        title: item.title,
        customer: item.customer,
        type: item.type,
        status: item.status || 'In Progress',
        budget: item.budget,
        vendorCost: item.vendorCost,
        price: item.vendorCost + item.vendorCost * 0.3,
        deliveryDate: item.deliveryDate || '',
        createdDate: new Date(item.createdDate),
        lastUpdatedDate: item.currentDate
          ? new Date(item.currentDate).toLocaleDateString()
          : '',
        lead: item.lead || 'sales',
        owner: item.owner?.name || 'Unknown',
        comment: item.comment || 'N/A',
        acceptForBid: item.acceptForBid || false,
        completed: item.completed || false,
        stgplan: item.stgplan,
        stgres: item.stgres,
        stgdel: item.stgdel,
        notifications: item.notifications || [],
        vendorStatus,
        revisionRequested: !!item.invited?.[team]?.revision_requested,
        revisedBudget: item.invited?.[team]?.revised_budget ?? undefined,
        category,
      };
    });
  }

  // ─── Filtered getters (reactive) ───────────────────────────────
  getInvitedProjects(): Observable<MappedProject[]> {
    return this.projects$.pipe(
      map((p) => p.filter((x) => x.category === 'invited')),
    );
  }

  getAssignedProjects(): Observable<MappedProject[]> {
    return this.projects$.pipe(
      map((p) => p.filter((x) => x.category === 'assigned')),
    );
  }

  getCompletedProjects(): Observable<MappedProject[]> {
    return this.projects$.pipe(
      map((p) => p.filter((x) => x.category === 'completed')),
    );
  }

  getActiveProjects(): Observable<MappedProject[]> {
    return this.projects$.pipe(
      map((p) =>
        p.filter(
          (x) => x.category === 'assigned' || x.category === 'inprogress',
        ),
      ),
    );
  }

  getAllProjects(): Observable<MappedProject[]> {
    return this.projects$;
  }

  // All active projects the vendor was NOT personally invited to (global open bid pool)
  getOpenBidProjects(): Observable<MappedProject[]> {
    return this.projects$.pipe(
      map((p) => p.filter((x) => x.category === 'other' && !x.completed)),
    );
  }

  // ─── Self-invite: 3-step flow matching roles.component.ts ────────
  selfInvite(project: MappedProject): Observable<any> {
    const user = this.getUser() || this.loadUser();
    const invitePayload = {
      mailto: user.email,
      projectid: project.id,
      project_name: project.title,
      location: '',
      deadline: project.deliveryDate,
      budget: project.vendorCost?.toString() || '0',
      login_url: `${environment.vendorApiBase}/sign-in`,
      vendor_name: user.name,
      vendor: user.team,
    };
    return this.http
      .post(`${this.apiUrl}projects/invite-vendor`, invitePayload)
      .pipe(
        switchMap(() => {
          const notifPayload = {
            project_id: project.id,
            recipient_emails: [user.email],
            type: 'invite',
          };
          return this.http
            .post(`${this.apiUrl}notifications/send`, notifPayload)
            .pipe(catchError(() => of(null)));
        }),
        switchMap(() =>
          this.http.post(
            `${this.apiUrl}vendors/${user.team}/projects/${project.id}`,
            { invited: true, assigned: false, rejected: false, onHold: false, abandoned: false, cancelLost: false, expires: false, resumed: false },
          ),
        ),
      );
  }

  // ─── Check if vendor has work samples matching the project type ────
  // API: GET /vendors/{vendorCode}/samples?category={projectType}
  // Response: { samples: [{ id, title, category, status, file_url, thumbnail_url, uploaded_at }] }
  // Returns: true = has approved samples, false = no matching samples, null = API not yet available
  checkVendorSamples(
    vendorCode: string,
    projectType: string,
  ): Observable<boolean | null> {
    const category = encodeURIComponent(projectType);
    return this.http
      .get<{
        samples: { category: string; status?: string }[];
      }>(`${this.apiUrl}vendors/${vendorCode}/samples?category=${category}`)
      .pipe(
        map((data) => {
          const samples = data?.samples || [];
          const type = projectType.toLowerCase();
          const match = samples.some((s) => {
            const cat = (s.category || '').toLowerCase();
            const status = (s.status || 'approved').toLowerCase();
            return (
              (cat.includes(type) || type.includes(cat)) &&
              status !== 'rejected'
            );
          });
          return match;
        }),
        catchError(() => of(null)), // null = API not yet built; treat as "no block"
      );
  }

  // ─── Update project status ─────────────────────────────────────
  updateProjectStatus(projectId: string, status: string): Observable<any> {
    return this.http.patch(`${this.apiUrl}projects/${projectId}/status`, {
      status,
    });
  }

  // ─── Vendor profile (submission-mahatta DB) ─────────────────────
  private get vendorAuthHeaders(): HttpHeaders {
    const token = localStorage.getItem('accessToken') || '';
    return new HttpHeaders({
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    });
  }

  getBanks(): Observable<Bank[]> {
    const base = environment.vendorBaseUri;
    return this.http.get<any>(`${base}/unauth/bank`).pipe(
      map((res) => res?.data ?? res ?? []),
      catchError((err) => {
        console.error('Error fetching banks:', err);
        return of([]);
      }),
    );
  }

  getVendorProfile(): Observable<VendorProfile | null> {
    const base = environment.vendorBaseUri;
    return this.http
      .get<any>(`${base}/auth/profile`, { headers: this.vendorAuthHeaders })
      .pipe(
        map((res) => res?.data ?? res),
        tap((profile: VendorProfile | null) => {
          if (profile) {
            this.basicProfileCompletedSubject.next(
              !!profile.is_basic_details_completed,
            );
            if (profile.contributor_id) {
              this.contributorId = profile.contributor_id;
            }
            this.syncProgressFromProfile(profile);
          }
        }),
        catchError((err) => {
          console.error('Error fetching vendor profile:', err);
          return of(null);
        }),
      );
  }

  updateVendorProfile(profile: Partial<VendorProfile>): Observable<any> {
    const base = environment.vendorBaseUri;
    return this.http
      .put<any>(`${base}/auth/profile`, profile, {
        headers: this.vendorAuthHeaders,
      })
      .pipe(
        catchError((err) => {
          console.error('Error updating vendor profile:', err);
          throw err;
        }),
      );
  }

  // ─── Identity profile (short bio) ──────────────────────────────
  getIdentityProfile(): Observable<any> {
    const base = environment.vendorBaseUri;
    return this.http
      .get<any>(`${base}/auth/identity-profile`, {
        headers: this.vendorAuthHeaders,
      })
      .pipe(
        map((res) => res?.data ?? res),
         tap((profile: any) => {
        if (profile) {
          this.syncProgressFromIdentity(profile);
        }
       }),
        catchError((err) => {
          console.error('Error fetching identity profile:', err);
          return of(null);
        }),
      );
  }

  saveIdentityProfile(payload: {
    short_bio: string;
    short_bio_ai_json: any;
    location_availability?: number[];
  }): Observable<any> {
    const base = environment.vendorBaseUri;
    return this.http
      .post<any>(`${base}/auth/identity-profile`, payload, {
        headers: this.vendorAuthHeaders,
      })
      .pipe(
        catchError((err) => {
          console.error('Error saving identity profile:', err);
          throw err;
        }),
      );
  }

  evaluateBio(payload: {
    vendor_name: string;
    bio_text: string;
    contributor_id: number | null;
  }): Observable<any> {
    const base = environment.vendorBaseUri;
    return this.http
      .post<any>(`${base}/unauth/vendor/bio/evaluate`, payload)
      .pipe(
        map((res: any) => res?.data ?? res),
        catchError((err) => {
          console.error('Error evaluating bio:', err);
          throw err;
        }),
      );
  }

  // ─── SSE Bio Chat (Smart Scope Engine) ─────────────────────────
  private get sseHeaders() {
    return {
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': environment.sseApiKey,
      },
    };
  }

  bioChatSend(payload: {
    vendor_id: string;
    message: string;
    history: { role: string; content: string }[];
    context?: any;
  }): Observable<{
    reply: string;
    bio_assembled: string | null;
    done: boolean;
  }> {
    const base = environment.sseBaseUri;
    return this.http
      .post<any>(`${base}/vendor/onboard/bio/chat`, payload, this.sseHeaders)
      .pipe(
        catchError((err) => {
          console.error('Error in bio chat:', err);
          throw err;
        }),
      );
  }

  /**
   * Streaming bio-chat: opens a Server-Sent Events stream so the assistant's
   * reply flows token-by-token instead of arriving as one block after a 5-10s wait.
   *
   *  - onText(chunk):  called for each piece of assistant text as it's produced.
   *  - onDone(final):  called once the agent finishes — `final` has the same
   *                    shape as bioChatSend's response (reply, bio_assembled,
   *                    done, extracted, missing_required, missing_optional).
   *  - onError(err):   called on transport / server failure.
   *
   * Returns an AbortController so the caller can cancel mid-stream
   * (e.g. if the user closes the page).
   */
  bioChatSendStream(
    payload: {
      vendor_id: string;
      message: string;
      history: { role: string; content: string }[];
      context?: any;
    },
    onText: (chunk: string) => void,
    onDone: (final: any) => void,
    onError: (err: any) => void,
    // ── USP flow (vendor-onboarding-agent) ─────────────────────────────
    // Optional callbacks — if a caller doesn't care about the USP + Evidence
    // side-channel, it just doesn't pass them and the stream behaves as
    // before. When present, these fire once per matching SSE frame emitted
    // by the bio-chat agent's record_usp / request_evidence tools.
    onEvidenceRequest?: (data: {
      for_service: string;
      usp_claim: string;
      sample_type_hint: string;
      retry_count: number;
      max_retries: number;
    }) => void,
    onUspCaptured?: (data: {
      for_service: string;
      claim: string;
      detail: string;
    }) => void,
    onUspEscalated?: (data: {
      for_service: string;
      reason: string;
    }) => void,
  ): AbortController {
    const controller = new AbortController();
    const base = environment.sseBaseUri;
    const apiKey =
      (this.sseHeaders.headers as any)['X-API-Key'] || environment.sseApiKey;

    fetch(`${base}/vendor/onboard/bio/chat/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': apiKey,
      },
      body: JSON.stringify(payload),
      signal: controller.signal,
    })
      .then(async (response) => {
        if (!response.ok || !response.body) {
          throw new Error(`HTTP ${response.status}`);
        }
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        // SSE frame parser: events are separated by blank lines (\n\n);
        // each event has lines starting with "event:" and/or "data:".
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          let frameEnd: number;
          while ((frameEnd = buffer.indexOf('\n\n')) !== -1) {
            const frame = buffer.slice(0, frameEnd);
            buffer = buffer.slice(frameEnd + 2);
            if (!frame.trim()) continue;

            let eventType = 'message';
            const dataLines: string[] = [];
            for (const line of frame.split('\n')) {
              if (line.startsWith('event:')) {
                eventType = line.slice(6).trim();
              } else if (line.startsWith('data:')) {
                dataLines.push(line.slice(5).trim());
              }
            }
            const data = dataLines.join('\n');
            if (!data) continue;

            try {
              const parsed = JSON.parse(data);
              if (eventType === 'text') {
                onText(parsed.text || '');
              } else if (eventType === 'done') {
                onDone(parsed);
              } else if (eventType === 'error') {
                onError(parsed.error || 'stream error');
              } else if (eventType === 'evidence_request') {
                onEvidenceRequest?.(parsed);
              } else if (eventType === 'usp_captured') {
                onUspCaptured?.(parsed);
              } else if (eventType === 'usp_escalated') {
                onUspEscalated?.(parsed);
              }
            } catch (e) {
              // Malformed event — skip
              console.warn('bioChatSendStream: bad SSE frame', frame);
            }
          }
        }
      })
      .catch((err) => {
        if ((err as any)?.name === 'AbortError') return;
        console.error('bioChatSendStream error:', err);
        onError(err);
      });

    return controller;
  }

  submitBioToSSE(payload: {
    vendor_id: string;
    bio: string;
    services: string[];
    // vendor-onboarding-agent — per-service polished bios captured
    // during chat when 2+ services were selected. Optional; single-
    // service submits omit it and the backend just persists `bio`.
    bio_per_service?: Record<string, string> | null;
  }): Observable<any> {
    const base = environment.sseBaseUri;
    return this.http
      .post<any>(`${base}/vendor/onboard/bio`, payload, this.sseHeaders)
      .pipe(
        catchError((err) => {
          console.error('Error submitting bio to SSE:', err);
          throw err;
        }),
      );
  }

  /**
   * vendor-onboarding-agent — Bio Manager per-service publish.
   * Overwrites bio_analysis.per_service_bios[<service>] with new prose,
   * bumps the version counter, and re-syncs Solr/Chroma.
   */
  publishServiceBio(payload: {
    vendor_id: string;
    service: string;
    bio: string;
  }): Observable<any> {
    const base = environment.sseBaseUri;
    return this.http.patch<any>(
      `${base}/vendor/onboard/${payload.vendor_id}/bio/${encodeURIComponent(payload.service)}`,
      { bio: payload.bio },
      this.sseHeaders,
    ).pipe(
      catchError((err) => {
        console.error('Error publishing service bio:', err);
        throw err;
      }),
    );
  }

  // ─── SSE Vendor contact (name / email / phone) ───────────────
  // Updates the vendor's contact fields across DB + Solr + Chroma in one call.
  // Send only the fields that changed; unsent fields are left untouched.
  updateVendorContact(payload: {
    vendor_id: string;
    name?: string;
    email?: string;
    mobile?: string;
  }): Observable<any> {
    const base = environment.sseBaseUri;
    return this.http
      .put<any>(`${base}/internal/vendor-contact`, payload, this.sseHeaders)
      .pipe(
        catchError((err) => {
          console.error('Error updating vendor contact on SSE:', err);
          throw err;
        }),
      );
  }

  getOnboardingStatus(vendorId: string): Observable<any> {
    const base = environment.sseBaseUri;
    return this.http
      .get<any>(`${base}/vendor/onboard/${vendorId}`, this.sseHeaders)
      .pipe(
        // Every read of onboarding state funnels through here — the stepper,
        // the dashboard, and refreshOnboardingStatus() all call it. So this is
        // the one place that sees a vendor's progress, which makes it the only
        // place worth instrumenting.
        //
        // This fires on EVERY fetch, including every page load. The tracker is
        // what makes that safe: it holds a per-vendor high-water mark and emits
        // only for stages newly crossed. Without that, `vendor_bio_updated`
        // would fire on every page view forever.
        tap((status) => this.onboardingTracker.statusObserved(vendorId, status)),
        catchError((err) => {
          console.error('Error fetching onboarding status:', err);
          return of(null);
        }),
      );
  }

  // ─── Instant-toast push channel ─────────────────────────────────────
  // Backend APIs that fire in-app triggers (e.g. PUT /auth/profile firing
  // basic_info_complete / skipped_financials, or POST /vendor/onboard/bio
  // firing bio_completed) now include the freshly fired rows inline in
  // their response under `notifications_fired`. Callers pipe those rows
  // through `pushFreshNotifications()` which fans them out via
  // `freshNotifications$` — vendor-console subscribes and pops a toast
  // in the same tick as the save returning. No NavigationEnd round-trip,
  // no waiting for the 60s poll.
  private _freshNotifications$ = new Subject<any>();
  readonly freshNotifications$ = this._freshNotifications$.asObservable();

  /** Fan out a batch of freshly-fired notifications to any subscribers
   *  (vendor-console listens). Safe to call with an empty / undefined
   *  array — that's a no-op. */
  pushFreshNotifications(notifications: any[] | undefined | null): void {
    if (!notifications || !notifications.length) return;
    notifications.forEach((n) => this._freshNotifications$.next(n));
  }

  /**
   * Shared "fresh onboarding state" stream. Components that ALREADY hold a
   * cached onboarding state (notably the vendor-console parent that drives
   * the sidebar progress widget) subscribe to this and apply whatever
   * arrives. Components that mutate onboarding state (e.g. vc-services
   * after a successful rate submit) push fresh data here before navigating
   * so the cache is updated *before* the destination page paints — no more
   * brief "Set your rates" flash on the dashboard right after submitting.
   */
  private _onboardingStatus$ = new BehaviorSubject<any>(null);
  readonly onboardingStatus$ = this._onboardingStatus$.asObservable();

  /**
   * Re-fetch the onboarding status and broadcast the result. Subscribers
   * also get the value via `onboardingStatus$`. Returns the same observable
   * so the caller can `await` (subscribe) before navigating.
   */
  refreshOnboardingStatus(vendorId: string): Observable<any> {
    return this.getOnboardingStatus(vendorId).pipe(
      tap((status) => {
        if (status) this._onboardingStatus$.next(status);
      }),
    );
  }

  getVendorSamples(vendorId: string): Observable<any[]> {
    const base = environment.sseBaseUri;
    return this.http
      .get<any>(`${base}/vendor/onboard/${vendorId}/samples`, this.sseHeaders)
      .pipe(
        map((res: any) => res?.samples ?? []),
        catchError((err) => {
          console.error('Error fetching vendor samples:', err);
          return of([]);
        }),
      );
  }

  getServices(contributorId: number) {
    const base = environment.sseBaseUri;
    return this.http.get<any>(
      `${base}/vendor/onboard/${contributorId}/services`,
      this.sseHeaders,
    );
  }

  sseSampleUpload(formData: FormData): Observable<any> {
    const base = environment.sseBaseUri;
    return this.http
      .post<any>(`${base}/vendor/onboard/sample/upload`, formData, {
        headers: { 'X-API-Key': environment.sseApiKey },
      })
      .pipe(
        catchError((err) => {
          console.error('Error uploading SSE sample:', err);
          throw err;
        }),
      );
  }

  // ─── SSE Signed-URL upload (bypasses Cloudflare's 100MB cap) ─────────────
  // The multipart sseSampleUpload above goes browser → Cloudflare → nginx →
  // backend, and Cloudflare blocks bodies over 100MB with a 413. For large
  // files (mainly videos), we instead PUT the file directly to GCS using a
  // signed URL the backend mints, then tell the backend the GCS path so it
  // can run the same analysis pipeline.

  sseGetSignedUploadUrl(mime: string, ext: string, mediaType: string): Observable<any> {
    const base = environment.sseBaseUri;
    return this.http
      .post<any>(
        `${base}/vendor/onboard/signed-url`,
        { mime, ext, media_type: mediaType },
        this.sseHeaders,
      )
      .pipe(
        catchError((err) => {
          console.error('Error getting SSE signed URL:', err);
          throw err;
        }),
      );
  }

  // Direct browser → GCS upload using a pre-signed PUT URL.
  // Do NOT add X-API-Key here — GCS validates the URL signature and the
  // Content-Type header must match what was passed to /signed-url, otherwise
  // GCS returns a SignatureDoesNotMatch error.
  sseUploadToSignedUrl(uploadUrl: string, file: File): Observable<any> {
    const contentType = file.type || 'application/octet-stream';
    return this.http
      .put<any>(uploadUrl, file, {
        headers: { 'Content-Type': contentType },
      })
      .pipe(
        catchError((err) => {
          console.error('Error uploading to GCS signed URL:', err);
          throw err;
        }),
      );
  }

  sseSampleFromGcs(payload: {
    vendor_id: string;
    gcs_path: string;
    service_type: string;
    sub_service: string;
    description: string;
    media_type: string;
    title?: string;
    skip_ai?: boolean;
  }): Observable<any> {
    const base = environment.sseBaseUri;
    return this.http
      .post<any>(`${base}/vendor/onboard/sample/gcs`, payload, this.sseHeaders)
      .pipe(
        catchError((err) => {
          console.error('Error finalizing GCS sample:', err);
          throw err;
        }),
      );
  }

  // Composite call: signed-url → PUT direct to GCS → POST /sample/gcs.
  // Returns the same response shape as sseSampleUpload so the caller can
  // subscribe identically. media_type is required by the backend (image | video | audio).
  sseSampleSignedUrlUpload(
    file: File,
    payload: {
      vendor_id: string;
      service_type: string;
      sub_service: string;
      description: string;
      media_type: string;
      title?: string;
    },
  ): Observable<any> {
    const ext = (file.name.split('.').pop() || '').toLowerCase();
    const mime = file.type || 'application/octet-stream';
    return this.sseGetSignedUploadUrl(mime, ext, payload.media_type).pipe(
      switchMap((urlRes: any) => {
        const uploadUrl = urlRes?.upload_url;
        const gcsPath = urlRes?.file_path;
        if (!uploadUrl || !gcsPath) {
          throw new Error('signed-url response missing upload_url or file_path');
        }
        return this.sseUploadToSignedUrl(uploadUrl, file).pipe(
          switchMap(() =>
            this.sseSampleFromGcs({
              ...payload,
              gcs_path: gcsPath,
            }),
          ),
        );
      }),
    );
  }

  // Replace media + metadata (new file required)
  sseSampleUpdate(vendorId: string, sampleId: string, formData: FormData): Observable<any> {
    const base = environment.sseBaseUri;
    return this.http
      .put<any>(`${base}/vendor/onboard/${vendorId}/sample/${sampleId}/upload`, formData, {
        headers: { 'X-API-Key': environment.sseApiKey },
      })
      .pipe(
        catchError((err) => {
          console.error('Error updating SSE sample:', err);
          throw err;
        }),
      );
  }

  // Update metadata only (no new file — re-analyzes using existing GCS file)
  sseSampleUpdateMeta(vendorId: string, sampleId: string, payload: {
    vendor_id: string; service_type: string; sub_service?: string; description?: string;
  }): Observable<any> {
    const base = environment.sseBaseUri;
    return this.http
      .put<any>(`${base}/vendor/onboard/${vendorId}/sample/${sampleId}`, payload, this.sseHeaders)
      .pipe(
        catchError((err) => {
          console.error('Error updating SSE sample metadata:', err);
          throw err;
        }),
      );
  }

  /** Re-file samples under a different sub-service without re-running the
   *  AI. One of the two remedies the post-upload alignment notice offers
   *  when samples don't match the sub-service they were uploaded under. */
  sseRefileSamples(vendorId: string, sampleIds: string[], subService: string): Observable<any> {
    const base = environment.sseBaseUri;
    return this.http
      .post<any>(
        `${base}/vendor/onboard/${vendorId}/samples/refile`,
        { sample_ids: sampleIds, sub_service: subService },
        this.sseHeaders,
      )
      .pipe(
        catchError((err) => {
          console.error('Error re-filing SSE samples:', err);
          throw err;
        }),
      );
  }

  // ── Specialty coverage (post-USP refactor) ────────────────────────────
  // Powers the Samples page's Specialty Coverage widget + the Bio Manager's
  // chip-based specialty editor. All three call the same backend endpoints
  // and share the same coverage shape returned from `/specialty-coverage`.

  sseGetSpecialtyCoverage(vendorId: string): Observable<any> {
    const base = environment.sseBaseUri;
    return this.http
      .get<any>(`${base}/vendor/onboard/${vendorId}/specialty-coverage`, this.sseHeaders)
      .pipe(
        catchError((err) => {
          console.error('Error fetching specialty coverage:', err);
          throw err;
        }),
      );
  }

  sseAddSpecialtyToBio(vendorId: string, service: string, specialty: string): Observable<any> {
    const base = environment.sseBaseUri;
    const encodedService = encodeURIComponent(service);
    return this.http
      .post<any>(
        `${base}/vendor/onboard/${vendorId}/bio/${encodedService}/specialty`,
        { specialty },
        this.sseHeaders,
      )
      .pipe(
        catchError((err) => {
          console.error('Error adding specialty to bio:', err);
          throw err;
        }),
      );
  }

  sseRemoveSpecialtyFromBio(vendorId: string, service: string, specialty: string): Observable<any> {
    const base = environment.sseBaseUri;
    const encodedService = encodeURIComponent(service);
    const params = new HttpParams().set('specialty', specialty);
    return this.http
      .delete<any>(
        `${base}/vendor/onboard/${vendorId}/bio/${encodedService}/specialty`,
        { ...this.sseHeaders, params },
      )
      .pipe(
        catchError((err) => {
          console.error('Error removing specialty from bio:', err);
          throw err;
        }),
      );
  }

  sseSampleBatchUpload(formData: FormData): Observable<any> {
    const base = environment.sseBaseUri;
    return this.http
      .post<any>(`${base}/vendor/onboard/sample/batch`, formData, {
        headers: { 'X-API-Key': environment.sseApiKey },
      })
      .pipe(
        catchError((err) => {
          console.error('Error batch uploading SSE samples:', err);
          throw err;
        }),
      );
  }

  // Batch finalize after each file was PUT directly to GCS via signed URL.
  // Used when total batch body would exceed Cloudflare's ~100MB cap.
  sseSampleBatchFromGcs(payload: {
    vendor_id: string;
    service_type: string;
    sub_service: string;
    description: string;
    title?: string;
    items: Array<{ gcs_path: string; filename: string; content_type: string }>;
  }): Observable<any> {
    const base = environment.sseBaseUri;
    return this.http
      .post<any>(`${base}/vendor/onboard/sample/batch/from-gcs`, payload, this.sseHeaders)
      .pipe(
        catchError((err) => {
          console.error('Error finalizing GCS batch:', err);
          throw err;
        }),
      );
  }

  // Composite: parallel signed-URL → PUT to GCS for each file → POST batch/from-gcs.
  // Returns a normal sseSampleBatchFromGcs response augmented with `_batchInfo`
  // describing per-file outcomes — so callers can show partial-success UI
  // instead of treating the whole batch as a binary success/fail.
  //
  // Why the change matters: the previous version used `forkJoin` over per-file
  // upload chains, which cancels everything if any one chain errors. So if 1
  // of 10 GCS PUTs failed, the other 9 (already uploaded to GCS) became
  // orphan objects on the bucket and the user saw "Upload failed" with no
  // way to recover except retrying — producing duplicate orphans each time.
  //
  // Now: each per-file chain catches its own error, returning a {success:
  // false, ...} marker instead of bubbling. After all settle, we partition,
  // finalize only the successful ones, and report exactly what failed.
  sseSampleBatchSignedUrlUpload(
    files: File[],
    payload: {
      vendor_id: string;
      service_type: string;
      sub_service: string;
      description: string;
      title?: string;
    },
  ): Observable<any> {
    type PerFileSuccess = {
      success: true;
      gcs_path: string;
      filename: string;
      content_type: string;
    };
    type PerFileFailure = {
      success: false;
      filename: string;
      error: string;
    };
    type PerFileResult = PerFileSuccess | PerFileFailure;

    const uploadOne$ = (file: File): Observable<PerFileResult> => {
      const ext = (file.name.split('.').pop() || '').toLowerCase();
      const mime = file.type || 'image/jpeg';
      return this.sseGetSignedUploadUrl(mime, ext, 'image').pipe(
        switchMap((urlRes: any) => {
          const uploadUrl = urlRes?.upload_url;
          const gcsPath = urlRes?.file_path;
          if (!uploadUrl || !gcsPath) {
            throw new Error('signed-url response missing upload_url or file_path');
          }
          return this.sseUploadToSignedUrl(uploadUrl, file).pipe(
            map(
              (): PerFileSuccess => ({
                success: true,
                gcs_path: gcsPath,
                filename: file.name,
                content_type: mime,
              }),
            ),
          );
        }),
        // Catch per-file errors so one bad file doesn't take the batch down.
        catchError(
          (err: any): Observable<PerFileFailure> =>
            of({
              success: false,
              filename: file.name,
              error:
                err?.message ||
                err?.error?.detail ||
                err?.statusText ||
                'Upload failed',
            }),
        ),
      );
    };

    return forkJoin(files.map(uploadOne$)).pipe(
      switchMap((results) => {
        const successes = results.filter(
          (r): r is PerFileSuccess => r.success,
        );
        const failures = results.filter(
          (r): r is PerFileFailure => !r.success,
        );

        // Nothing made it to GCS → no point calling finalize. Surface a
        // clear error so the caller shows the right message and the user
        // can retry the original files.
        if (successes.length === 0) {
          const sample = failures[0]?.error || 'Upload failed';
          throw new Error(
            `Couldn't upload any files (${failures.length} failed). ${sample}`,
          );
        }

        const items = successes.map((s) => ({
          gcs_path: s.gcs_path,
          filename: s.filename,
          content_type: s.content_type,
        }));

        return this.sseSampleBatchFromGcs({ ...payload, items }).pipe(
          map((finalize: any) => ({
            // Preserve every existing field of the finalize response so
            // existing callers (that read `count`, `sample_id`, etc.)
            // keep working.
            ...finalize,
            // New: per-batch outcome metadata for partial-success UI.
            _batchInfo: {
              totalRequested: files.length,
              successCount: successes.length,
              failedFiles: failures.map((f) => ({
                name: f.filename,
                error: f.error,
              })),
            },
          })),
          catchError((err: any) => {
            // Finalize failed after GCS uploads succeeded — those objects
            // are now orphan on the bucket. Tag the error so the caller
            // can show a distinct, accurate message ("uploaded but not
            // saved") instead of pretending nothing happened.
            const tagged: any = err instanceof Error ? err : new Error(String(err));
            tagged._gcsUploadedButFinalizeFailed = true;
            tagged._uploadedCount = successes.length;
            tagged._gcsPaths = successes.map((s) => s.gcs_path);
            throw tagged;
          }),
        );
      }),
    );
  }

  sseSampleDelete(vendorId: string, sampleId: string): Observable<any> {
    const base = environment.sseBaseUri;
    return this.http
      .delete<any>(
        `${base}/vendor/onboard/${vendorId}/sample/${sampleId}`,
        this.sseHeaders,
      )
      .pipe(
        catchError((err) => {
          console.error('Error deleting SSE sample:', err);
          throw err;
        }),
      );
  }

  // ─── SSE Rates ───────────────────────────────────────────────
  submitRates(payload: {
    vendor_id: string;
    currency: string;
    services: { service: string; project_rate_min: number | null; project_rate_max: number | null; day_rate: number | null }[];
    notes?: string;
  }): Observable<any> {
    const base = environment.sseBaseUri;
    return this.http
      .post<any>(`${base}/vendor/onboard/rate`, payload, this.sseHeaders)
      .pipe(
        catchError((err) => {
          console.error('Error submitting rates:', err);
          throw err;
        }),
      );
  }

  // ─── Cities / Locations ────────────────────────────────────────
  getCities(): Observable<any[]> {
    const base = environment.vendorBaseUri;
    return this.http.get<any>(`${base}/unauth/city?pageSize=-1`).pipe(
      map((res: any) => res?.data ?? res ?? []),
      catchError((err) => {
        console.error('Error fetching cities:', err);
        return of([]);
      }),
    );
  }

  // ─── Showreels / Samples ────────────────────────────────────────
  private get vendorAuthHeadersMultipart(): HttpHeaders {
    const token = localStorage.getItem('accessToken') || '';
    return new HttpHeaders({ Authorization: `Bearer ${token}` });
  }

  getStudioCategories(): Observable<any[]> {
    const base = environment.vendorBaseUri;
    return this.http.get<any>(`${base}/unauth/studio-categories`).pipe(
      map((res: any) => res?.data ?? res ?? []),
      catchError((err) => {
        console.error('Error fetching studio categories:', err);
        return of([]);
      }),
    );
  }

  getContributorSamples(contributorId: number): Observable<any[]> {
    const base = environment.vendorBaseUri;
    return this.http
      .get<any>(`${base}/unauth/contributor/${contributorId}/samples`, {
        headers: this.vendorAuthHeaders,
      })
      .pipe(
        map((res: any) => res?.data ?? res ?? []),
        catchError((err) => {
          console.error('Error fetching samples:', err);
          return of([]);
        }),
      );
  }

  addContributorSample(formData: FormData): Observable<any> {
    const base = environment.vendorBaseUri;
    return this.http
      .post<any>(
        `${base}/unauth/studio-contributor-subcategory/store`,
        formData,
        { headers: this.vendorAuthHeadersMultipart },
      )
      .pipe(
        map((res: any) => res?.data ?? res),
        catchError((err) => {
          console.error('Error adding sample:', err);
          throw err;
        }),
      );
  }

  updateContributorSample(id: number, formData: FormData): Observable<any> {
    formData.append('portfolio_id', String(id));
    const base = environment.vendorBaseUri;
    return this.http
      .post<any>(
        `${base}/unauth/studio-contributor-subcategory/store`,
        formData,
        { headers: this.vendorAuthHeadersMultipart },
      )
      .pipe(
        map((res: any) => res?.data ?? res),
        catchError((err) => {
          console.error('Error updating sample:', err);
          throw err;
        }),
      );
  }

  editContributorSample(data: {
    sample_id: number;
    contributor_id: number;
    studio_service_ids: string;
    description: string;
    other_sub_service?: string;
  }): Observable<any> {
    const base = environment.vendorBaseUri;
    return this.http
      .put<any>(`${base}/unauth/studio-contributor-subcategory/edit`, data, {
        headers: this.vendorAuthHeaders,
      })
      .pipe(
        map((res: any) => res?.data ?? res),
        catchError((err) => {
          console.error('Error editing sample:', err);
          throw err;
        }),
      );
  }

  deleteContributorSample(id: number): Observable<any> {
    const base = environment.vendorBaseUri;
    return this.http
      .delete<any>(
        `${base}/auth/studio-contributor-subcategory/deleteSample/${id}`,
        { headers: this.vendorAuthHeaders },
      )
      .pipe(
        catchError((err) => {
          console.error('Error deleting sample:', err);
          throw err;
        }),
      );
  }

  // ─── Contributor Category Details (project value range) ────────────
  getContributorCategoryDetails(contributorId: number): Observable<any[]> {
    const base = environment.vendorBaseUri;
    return this.http
      .get<any>(`${base}/auth/contributor/${contributorId}/category-details`, {
        headers: this.vendorAuthHeaders,
      })
      .pipe(
        map((res) => res?.data ?? res ?? []),
        catchError((err) => {
          console.error('Error fetching category details:', err);
          return of([]);
        }),
      );
  }

  saveContributorCategoryDetail(
    contributorId: number,
    categoryId: number,
    payload: { project_value_min: number; project_value_max: number },
  ): Observable<any> {
    const base = environment.vendorBaseUri;
    return this.http
      .post<any>(
        `${base}/auth/contributor/${contributorId}/category-details`,
        { category_id: categoryId, ...payload },
        { headers: this.vendorAuthHeaders },
      )
      .pipe(
        map((res) => res?.data ?? res),
        catchError((err) => {
          console.error('Error saving category detail:', err);
          throw err;
        }),
      );
  }

  markContributorRegistered(): Observable<any> {
    const base = environment.vendorBaseUri;
    return this.http
      .post<any>(
        `${base}/auth/contributor/mark-registered`,
        {},
        { headers: this.vendorAuthHeaders },
      )
      .pipe(
        catchError((err) => {
          console.error('Error marking registered:', err);
          throw err;
        }),
      );
  }

  getVideoSignedUrl(mimeType: string, ext: string): Observable<any> {
    const base = environment.vendorBaseUri;
    return this.http
      .post<any>(`${base}/unauth/gcs/signed-url`, { mime: mimeType, ext })
      .pipe(
        map((res) => res?.data ?? res),
        catchError((err) => {
          console.error('Error getting signed URL:', err);
          throw err;
        }),
      );
  }

  // ─── Vendor Notifications ─────────────────────────────────────
  getVendorNotifications(contributorId: number): Observable<any[]> {
    const base = environment.vendorBaseUri;
    return this.http
      .get<any>(`${base}/auth/vendor-notifications/${contributorId}`, {
        headers: this.vendorAuthHeaders,
      })
      .pipe(
        map((res) => res?.data ?? res ?? []),
        catchError((err) => {
          console.error('Error fetching vendor notifications:', err);
          return of([]);
        }),
      );
  }

  getVendorUnreadCount(contributorId: number): Observable<number> {
    const base = environment.vendorBaseUri;
    return this.http
      .get<any>(
        `${base}/auth/vendor-notifications/${contributorId}/unread-count`,
        { headers: this.vendorAuthHeaders },
      )
      .pipe(
        map((res) => res?.unread_count ?? 0),
        catchError((err) => {
          console.error('Error fetching unread count:', err);
          return of(0);
        }),
      );
  }

  markVendorNotificationAsRead(
    contributorId: number,
    notificationId: number,
  ): Observable<any> {
    const base = environment.vendorBaseUri;
    return this.http
      .post<any>(
        `${base}/auth/vendor-notifications/${contributorId}/${notificationId}/read`,
        {},
        { headers: this.vendorAuthHeaders },
      )
      .pipe(
        catchError((err) => {
          console.error('Error marking notification as read:', err);
          throw err;
        }),
      );
  }

  markAllVendorNotificationsAsRead(contributorId: number): Observable<any> {
    const base = environment.vendorBaseUri;
    return this.http
      .post<any>(
        `${base}/auth/vendor-notifications/${contributorId}/mark-all-read`,
        {},
        { headers: this.vendorAuthHeaders },
      )
      .pipe(
        catchError((err) => {
          console.error('Error marking all as read:', err);
          throw err;
        }),
      );
  }

  // ─── Utility ───────────────────────────────────────────────────
  formatCurrency(amount: number, includeSymbol: boolean = true): string {
    if (!amount && amount !== 0) return includeSymbol ? '₹0' : '0';
    const formatter = new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
      currencyDisplay: 'symbol',
    });
    const formatted = formatter.format(amount);
    return includeSymbol ? formatted : formatted.replace('₹', '').trim();
  }

  formatDate(dateStr: string): string {
    if (!dateStr) return 'Not set';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return 'Invalid';
    return d.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  }

  getRelativeDate(date: string | Date): string {
    const d = new Date(date);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays <= 7) return `${diffDays} days ago`;
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
  }

  // Add to VendorConsoleService
  getProjectById(projectId: string): Observable<SmartScopeProjectData> {
    return this.http.get<SmartScopeProjectData>(
      `${this.apiUrl}projects/${projectId}`,
    );
  }

  // ─── Profile Progress Methods ───────────────────────────────────

  private getDefaultProgress(): ProfileProgress {
    return {
      overallPercent: 0,
      sections: {
        basic: {
          completed: false,
          fields: { name: false, email: false, phone: false, altPhone: false, altEmail: false, whatsapp: false },
          requiredFields: ['name', 'email', 'phone'],
          weight: 0.25
        },
        financial: {
          completed: false,
          fields: { paymentMethod: false, panCard: false, accountDetails: false },
          requiredFields: ['paymentMethod'],
          weight: 0.15
        },
        identity: {
          completed: false,
          fields: { shortBio: false, locationAvailability: false },
          requiredFields: ['shortBio'],
          weight: 0.25
        },
        showreels: {
          completed: false,
          fields: { hasSamples: false, minSamplesReached: false, aiEvaluated: false },
          requiredFields: ['hasSamples'],
          weight: 0.35
        }
      },
      isBidReady: false,
      missingRequirements: ['Full name', 'Email address', 'Phone number', 'Short bio (min 200 characters)', 'At least 1 showreel sample'],
      nextSteps: ['Complete basic profile information (name, email, phone)']
    };
  }

  private calculateProgress(): void {
    const basic = this.basicProfileFieldsSubject.value;
    const financial = this.financialFieldsSubject.value;
    const identity = this.identityFieldsSubject.value;
    const showreels = this.showreelsFieldsSubject.value;

    // Basic Info (25%) - REQUIRED: name, email, phone
    const basicFields = {
      name: !!basic.name,
      email: !!basic.email,
      phone: !!basic.phone,
      altPhone: !!basic.altPhone,
      altEmail: !!basic.altEmail,
      whatsapp: !!basic.whatsapp
    };
    
    const basicRequired = ['name', 'email', 'phone'];
    const basicCompleted = basicRequired.every(field => basicFields[field as keyof typeof basicFields]);
    const basicPercent = this.calculateSectionPercent(basicFields, basicRequired, 0.25);

    // Financial (15%) - REQUIRED: paymentMethod
    const financialFields = {
      paymentMethod: !!financial.paymentMethod,
      panCard: !!financial.panCard,
      accountDetails: !!financial.accountDetails
    };
    
    const financialRequired = ['paymentMethod'];
    const financialCompleted = financialRequired.every(field => financialFields[field as keyof typeof financialFields]);
    const financialPercent = this.calculateSectionPercent(financialFields, financialRequired, 0.15);

    // Identity (25%) - REQUIRED: shortBio
    const identityFields = {
      shortBio: !!identity.shortBio,
      locationAvailability: !!identity.locationAvailability
    };
    
    const identityRequired = ['shortBio'];
    const identityCompleted = identityRequired.every(field => identityFields[field as keyof typeof identityFields]);
    const identityPercent = this.calculateSectionPercent(identityFields, identityRequired, 0.25);

    // Showreels (35%) - REQUIRED: hasSamples
    const showreelsFields = {
      hasSamples: !!showreels.hasSamples,
      minSamplesReached: !!showreels.minSamplesReached,
      aiEvaluated: !!showreels.aiEvaluated
    };
    
    const showreelsRequired = ['hasSamples'];
    const showreelsCompleted = showreelsRequired.every(field => showreelsFields[field as keyof typeof showreelsFields]);
    const showreelsPercent = this.calculateSectionPercent(showreelsFields, showreelsRequired, 0.35);

    const overallPercent = Math.round(
      basicPercent * 100 + financialPercent * 100 + identityPercent * 100 + showreelsPercent * 100
    );

    const isBidReady = basicCompleted && identityCompleted && showreelsCompleted;

    const missingRequirements = this.getMissingRequirements({
      basic: { fields: basicFields, required: basicRequired },
      financial: { fields: financialFields, required: financialRequired },
      identity: { fields: identityFields, required: identityRequired },
      showreels: { fields: showreelsFields, required: showreelsRequired }
    });

    const nextSteps = this.getNextSteps({
      basic: { completed: basicCompleted, fields: basicFields, required: basicRequired },
      financial: { completed: financialCompleted, fields: financialFields, required: financialRequired },
      identity: { completed: identityCompleted, fields: identityFields, required: identityRequired },
      showreels: { completed: showreelsCompleted, fields: showreelsFields, required: showreelsRequired }
    });

    const progress: ProfileProgress = {
      overallPercent,
      sections: {
        basic: { completed: basicCompleted, fields: basicFields, requiredFields: basicRequired, weight: 0.25 },
        financial: { completed: financialCompleted, fields: financialFields, requiredFields: financialRequired, weight: 0.15 },
        identity: { completed: identityCompleted, fields: identityFields, requiredFields: identityRequired, weight: 0.25 },
        showreels: { completed: showreelsCompleted, fields: showreelsFields, requiredFields: showreelsRequired, weight: 0.35 }
      },
      isBidReady,
      missingRequirements,
      nextSteps
    };

    this.profileProgressSubject.next(progress);
  }

  private calculateSectionPercent(
    fields: Record<string, boolean>, 
    requiredFields: string[], 
    weight: number
  ): number {
    const requiredCompleted = requiredFields.filter(field => fields[field]).length;
    const requiredPercent = requiredFields.length > 0 ? requiredCompleted / requiredFields.length : 0;
    
    const allFields = Object.keys(fields);
    const optionalFields = allFields.filter(f => !requiredFields.includes(f));
    
    if (optionalFields.length === 0) {
      return requiredPercent * weight;
    }
    
    const optionalCompleted = optionalFields.filter(field => fields[field]).length;
    const optionalBonus = (optionalCompleted / optionalFields.length) * 0.2;
    
    return Math.min(requiredPercent + optionalBonus, 1) * weight;
  }

  private getMissingRequirements(sections: any): string[] {
    const missing: string[] = [];
    
    if (!sections.basic.fields.name) missing.push('Full name');
    if (!sections.basic.fields.email) missing.push('Email address');
    if (!sections.basic.fields.phone) missing.push('Phone number');
    if (!sections.identity.fields.shortBio) missing.push('Short bio (min 200 characters)');
    if (!sections.showreels.fields.hasSamples) missing.push('At least 1 showreel sample');
    if (!sections.financial.fields.paymentMethod) missing.push('Payment method (bank/UPI/PayPal)');
    
    return missing;
  }

  private getNextSteps(sections: any): string[] {
    const steps: string[] = [];
    
    if (!sections.basic.completed) {
      const missingBasic = sections.basic.required.filter((f: string) => !sections.basic.fields[f]);
      if (missingBasic.includes('name')) steps.push('Add your full name');
      if (missingBasic.includes('email')) steps.push('Add your email address');
      if (missingBasic.includes('phone')) steps.push('Add your phone number');
    } else if (!sections.identity.completed) {
      steps.push('Write your short bio (min 200 characters)');
      if (!sections.identity.fields.locationAvailability) {
        steps.push('Add location availability for better matching');
      }
    } else if (!sections.showreels.completed) {
      steps.push('Upload at least 1 showreel sample');
      if (sections.showreels.fields.hasSamples && !sections.showreels.fields.minSamplesReached) {
        steps.push('Add 3+ samples for better project matching');
      }
    } else if (!sections.financial.completed) {
      steps.push('Add payment details to receive payouts');
    } else {
      if (!sections.showreels.fields.aiEvaluated) {
        steps.push('Get AI evaluation on your samples');
      }
      if (!sections.basic.fields.whatsapp) {
        steps.push('Add WhatsApp for faster communication');
      }
    }
    
    return steps.slice(0, 3);
  }

  // ─── Public Progress Update Methods ──────────────────────────────

  updateBasicProfileFields(fields: Partial<ProfileSectionProgress['basic']['fields']>): void {
    this.basicProfileFieldsSubject.next({ ...this.basicProfileFieldsSubject.value, ...fields });
    this.calculateProgress();
  }

  updateFinancialFields(fields: Partial<ProfileSectionProgress['financial']['fields']>): void {
    this.financialFieldsSubject.next({ ...this.financialFieldsSubject.value, ...fields });
    this.calculateProgress();
  }

  updateIdentityFields(fields: Partial<ProfileSectionProgress['identity']['fields']>): void {
    this.identityFieldsSubject.next({ ...this.identityFieldsSubject.value, ...fields });
    this.calculateProgress();
  }

  updateShowreelsFields(fields: Partial<ProfileSectionProgress['showreels']['fields']>): void {
    this.showreelsFieldsSubject.next({ ...this.showreelsFieldsSubject.value, ...fields });
    this.calculateProgress();
  }

  getCurrentProgress(): ProfileProgress {
    return this.profileProgressSubject.value;
  }

  resetProfileProgress(): void {
    this.basicProfileFieldsSubject.next({});
    this.financialFieldsSubject.next({});
    this.identityFieldsSubject.next({});
    this.showreelsFieldsSubject.next({});
    this.calculateProgress();
  }

  // ─── Sync Progress from API Data ─────────────────────────────────

  syncProgressFromProfile(profile: VendorProfile): void {
    const hasName = !!(profile.name);
    const hasEmail = !!(profile.email);
    const hasPhone = !!(profile.mobile);
    
    this.updateBasicProfileFields({
      name: hasName,
      email: hasEmail,
      phone: hasPhone,
      altPhone: !!profile.alt_phone_no,
      altEmail: !!profile.alt_email,
      whatsapp: !!profile.whatsapp
    });

    const hasPaymentMethod = !!(profile.bank_id || profile.upi || profile.paypal);
    this.updateFinancialFields({
      paymentMethod: hasPaymentMethod,
      panCard: !!profile.pan_card,
      accountDetails: !!(profile.account_number && profile.ifsc)
    });
  }

  syncProgressFromIdentity(profile: any): void {
    this.updateIdentityFields({
      shortBio: !!(profile?.short_bio && profile.short_bio.length >= 200),
      locationAvailability: !!(profile?.location_availability?.length > 0)
    });
  }

  syncProgressFromSamples(samples: any[]): void {
    const hasSamples = samples.length > 0;
    const minSamplesReached = samples.length >= 3;
    const aiEvaluated = samples.some((s: any) => 
      s.ai_production_score != null || s.ai_creative_score != null
    );
    
    this.updateShowreelsFields({
      hasSamples,
      minSamplesReached,
      aiEvaluated
    });
  }

  // Add this method to get combined profile progress data
  loadProfileProgress(): Observable<ProfileProgress> {
    return forkJoin({
      profile: this.getVendorProfile(),
      identity: this.getIdentityProfile(),
    }).pipe(
      map(() => this.getCurrentProgress())
    );
  }

// Method to update proposal acceptance status
updateProposalStatus(projectId: string, accepted: boolean): Observable<any> {
  const user = this.getUser() || this.loadUser();
  const payload = {
    vendorCode: user.team,
    projectId: projectId,
    proposalAccepted: accepted,
    timestamp: new Date().toISOString()
  };
  
  return this.http
    .patch(`${this.apiUrl}vendors/${user.team}/projects/${projectId}/proposal-status`, payload)
    .pipe(
      tap(() => {
        // Refresh stats after update
        this.fetchVendorStats().subscribe();
      }),
      catchError((err) => {
        console.error('Error updating proposal status:', err);
        throw err;
      })
    );
}

getVendorStatsValue(): VendorStats | null {
  return this.vendorStatsSubject.value;
}
}

